/**
 * File storage — a tenant's own bucket, and proof that it works.
 *
 * Attachments used to go wherever the server's environment pointed, which for
 * a multi-tenant product means every customer's documents in one bucket under
 * one retention policy and one bill. A tenant configures their own here.
 *
 * The test is the important half. "Credentials accepted" is not "files work":
 * a bucket can sign an upload and refuse the PUT, or accept everything and
 * return different bytes. So the button walks the whole path — presign, upload,
 * head, presign, download, byte-compare, delete — and shows which step broke,
 * in the provider's own words.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Database, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { describeError } from '@/lib/errorMessage';

interface StepResult {
  step: string;
  ok: boolean;
  detail: string;
  error?: string;
}

interface CheckResult {
  ok: boolean;
  bucketLabel: string;
  source: 'workspace' | 'platform' | 'local';
  steps: StepResult[];
  corsPolicy: unknown;
}

const STEP_LABEL: Record<string, string> = {
  presignPut: 'Sign an upload URL',
  upload: 'Upload 1 KB',
  head: 'Confirm size',
  presignGet: 'Sign a download URL',
  download: 'Download and compare bytes',
  delete: 'Delete the probe',
};

export interface StorageState {
  bucket: string;
  region: string;
  endpoint: string;
  forcePathStyle: boolean;
  enabled: boolean;
  accessKeyId: { isSet: boolean; masked: string | null };
  secretAccessKey: { isSet: boolean; masked: string | null };
}

export function StorageSection({
  state,
  summary,
  secretField,
  onChange,
  onSave,
  saving,
  endpointBase,
}: {
  state: StorageState;
  summary: string;
  /** Renders a write-only secret row, shared with the rest of the page. */
  secretField: (key: string, label: string, current: { isSet: boolean; masked: string | null }) => React.ReactNode;
  onChange: (patch: Partial<StorageState>) => void;
  /** Persists the section. Omitted on the platform page, which saves as a whole. */
  onSave?: () => void;
  saving?: boolean;
  /** `/settings/integrations` or `/platform/settings` — same shape either way. */
  endpointBase: string;
}) {
  const qc = useQueryClient();
  const [result, setResult] = useState<CheckResult | null>(null);

  const test = useMutation({
    mutationFn: async () => (await api.post<CheckResult>(`${endpointBase}/storage/test`, {})).data,
    onSuccess: (data) => {
      setResult(data);
      if (data.ok) toast.success(`All six checks passed against ${data.bucketLabel}`);
      else {
        const failed = data.steps.find((s) => !s.ok);
        toast.error(`${STEP_LABEL[failed?.step ?? ''] ?? 'A step'} failed — ${failed?.error ?? failed?.detail}`, {
          duration: 15_000,
        });
      }
      void qc.invalidateQueries({ queryKey: ['integration-status'] });
    },
    onError: (err: unknown) =>
      toast.error(describeError(err, 'The storage check could not run'), { duration: 15_000 }),
  });

  return (
    <section className="vt-surface flex flex-col gap-3 p-3" data-testid="storage-section">
      <div className="flex items-center gap-2">
        <Database className="size-4 vt-muted" />
        <h2 className="flex-1 text-[13px] font-medium">File storage (S3 or compatible)</h2>
        <Button
          variant="ghost"
          size="sm"
          data-testid="test-storage"
          disabled={test.isPending}
          onClick={() => test.mutate()}
        >
          {test.isPending ? <Loader2 className="animate-spin" /> : null}
          Test connection
        </Button>
      </div>

      <p className="text-[12px] vt-muted" data-testid="storage-summary">
        {summary}
      </p>

      <label className="flex items-center gap-2 text-[13px]">
        <input
          type="checkbox"
          data-testid="s3-enabled"
          checked={state.enabled}
          onChange={(e) => onChange({ enabled: e.target.checked })}
        />
        Use this bucket for this workspace’s files
      </label>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="s3-bucket">Bucket</Label>
          <Input
            id="s3-bucket"
            data-testid="s3-bucket"
            value={state.bucket}
            onChange={(e) => onChange({ bucket: e.target.value })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="s3-region">Region</Label>
          <Input
            id="s3-region"
            data-testid="s3-region"
            placeholder="ap-south-1"
            value={state.region}
            onChange={(e) => onChange({ region: e.target.value })}
          />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {secretField('s3.accessKeyId', 'Access key ID', state.accessKeyId)}
        {secretField('s3.secretAccessKey', 'Secret access key', state.secretAccessKey)}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="s3-endpoint">
            Endpoint <span className="font-normal vt-muted">— optional</span>
          </Label>
          <Input
            id="s3-endpoint"
            data-testid="s3-endpoint"
            placeholder="https://s3.eu-central-1.wasabisys.com"
            value={state.endpoint}
            onChange={(e) => onChange({ endpoint: e.target.value })}
          />
          <span className="text-[11px] vt-muted">
            For MinIO, Cloudflare R2, Wasabi, Spaces. Leave empty for AWS.
          </span>
        </div>
        <label className="flex items-center gap-2 self-end pb-2 text-[13px]">
          <input
            type="checkbox"
            data-testid="s3-path-style"
            checked={state.forcePathStyle}
            onChange={(e) => onChange({ forcePathStyle: e.target.checked })}
          />
          Force path-style addressing
        </label>
      </div>

      {result && (
        <div className="flex flex-col gap-1.5 rounded-[8px] border border-[var(--border)] p-2.5" data-testid="storage-check-result">
          <div className="flex items-center gap-2 text-[12px]">
            <Badge tone={result.ok ? 'accent' : 'danger'}>{result.ok ? 'passed' : 'failed'}</Badge>
            <span className="vt-muted">{result.bucketLabel}</span>
          </div>
          <ul className="flex flex-col gap-0.5">
            {result.steps.map((step) => (
              <li
                key={step.step}
                className="flex items-start gap-1.5 text-[12px]"
                data-testid={`storage-step-${step.step}`}
              >
                {step.ok ? (
                  <Check className="mt-0.5 size-3.5 shrink-0 text-[var(--accent)]" />
                ) : (
                  <X className="mt-0.5 size-3.5 shrink-0 text-[var(--danger)]" />
                )}
                <span className="font-medium">{STEP_LABEL[step.step] ?? step.step}</span>
                <span className="min-w-0 flex-1 vt-muted">
                  {step.detail}
                  {/* The provider's own words on the failing step — the only
                      thing that turns "it failed" into something fixable. */}
                  {step.error ? ` — ${step.error}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {onSave && (
        <div className="flex justify-end">
          <Button variant="primary" size="sm" data-testid="save-storage" disabled={saving} onClick={onSave}>
            {saving ? <Loader2 className="animate-spin" /> : <Check />}
            Save storage
          </Button>
        </div>
      )}

      <details className="text-[12px]">
        <summary className="cursor-pointer vt-muted">
          Bucket CORS configuration (required for browser uploads)
        </summary>
        <p className="mt-1.5 vt-muted">
          Presigned uploads go straight from the browser to the bucket. Without this rule the
          server-side test passes and every real upload fails.
        </p>
        <pre
          className="mt-1.5 overflow-x-auto rounded-[6px] bg-[var(--surface-2)] p-2 text-[11px]"
          data-testid="cors-policy"
        >
          {JSON.stringify(result?.corsPolicy ?? DEFAULT_CORS, null, 2)}
        </pre>
        <Button
          variant="ghost"
          size="sm"
          className="mt-1"
          onClick={() => {
            void navigator.clipboard
              .writeText(JSON.stringify(result?.corsPolicy ?? DEFAULT_CORS, null, 2))
              .then(() => toast.success('CORS policy copied'));
          }}
        >
          <Copy />
          Copy
        </Button>
      </details>
    </section>
  );
}

/** Shown before a test has run, so the rule is available while configuring. */
const DEFAULT_CORS = [
  {
    AllowedHeaders: ['*'],
    AllowedMethods: ['GET', 'PUT', 'HEAD'],
    AllowedOrigins: [typeof window === 'undefined' ? 'https://your-app-host' : window.location.origin],
    ExposeHeaders: ['ETag'],
    MaxAgeSeconds: 3000,
  },
];
